import React from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import Chart from 'react-apexcharts';

import { MAIN_COLOR } from "components/styles/constants.styles";
import { splitInTheMiddleSpace } from "pages/DashboardCards/utils";
import ChartCard from "pages/DashboardCards/ChartCard";

interface Props {
    data: { label: string; value: number; }[];
    title: string;
    sortedKeyName: 'label' | 'value';
    sortyType: 'asc' | 'desc' | 'none';
}

const MOCK_SERIES = [{ name: 'Value', data: [12, 3, 56, 6] }];
const MOCK_CATEGORIES = ['Op 1', 'Op 2', 'Op 4', 'Op 5'];

const getSeries = (values: number[]) => {
    return [{ name: 'Value', data: values }];
};

const getOptions = (operators: (string | string[])[]) => {
    const options = {
        chart: {
            parentHeightOffset: 0,
            toolbar: {
                show: false
            }
        },
        plotOptions: {
            bar: {
                barHeight: '45%',
                borderRadius: [3],
            }
        },
        grid: {
            show: false,
        },
        colors: [MAIN_COLOR.EXTRA_DARK],
        dataLabels: {
            enabled: false
        },
        xaxis: {
            categories: operators,
            style: {
                colors: '#6E6B7B',
            },
        },
        yaxis: {
            labels: {
                formatter: function (val: any) {
                    return `${val} LEI`;
                },
                style: {
                    colors: '#6E6B7B',
                    fontSize: '12px', fontFamily: "Montserrat,sans-serif"
                },
            }
        },
        tooltip: {
            style: {
                fontSize: '12px', fontFamily: "Montserrat,sans-serif"
            },
            shared: false,
        },
    };

    return options;
};

const getSortedValues = (operatorPerformances: { label: string; value: number; }[], sortedKeyName: string, sortyType: string) => {

    const sortedValue = operatorPerformances.sort((a, b) => {
        if (sortyType === 'desc') {
            if (a[sortedKeyName as keyof typeof a] > b[sortedKeyName as keyof typeof b]) {
                return -1;
            }
            if (a[sortedKeyName as keyof typeof a] < b[sortedKeyName as keyof typeof b]) {
                return 1;
            }
        }

        if (sortyType === 'asc') {
            if (a[sortedKeyName as keyof typeof a] < b[sortedKeyName as keyof typeof b]) {
                return -1;
            }
            if (a[sortedKeyName as keyof typeof a] > b[sortedKeyName as keyof typeof b]) {
                return 1;
            }
        }

        return 0;
    }).reduce((acc: { operators: (string | string[])[], values: number[]; }, item: { label: string; value: number; }) => {
        return {
            operators: [...acc.operators, splitInTheMiddleSpace(item.label, 8)],
            values: [...acc.values, item.value]
        };
    }, { operators: [], values: [] });

    return sortedValue;
};

const SingleVerticalBar = ({ data, title, sortedKeyName, sortyType }: Props) => {
    const { t } = useTranslation();

    if (!data) {
        return (
            <ChartCard
                title={t(title)}
                noAvailableData
            >
                <Chart
                    options={getOptions(MOCK_CATEGORIES)}
                    series={MOCK_SERIES}
                    type='bar'
                    height={380}
                />
            </ChartCard>
        );
    }

    const { operators, values } = getSortedValues(data, sortedKeyName, sortyType);
    const series = getSeries(values);
    const options = getOptions(operators);

    return (
        <ChartCard
            title={t(title)}
        >
            <Chart options={options} series={series} type='bar' height={380} />
        </ChartCard>
    );
};

export default SingleVerticalBar;